import { authService } from '@/api/auth';
import { ChangePasswordModal } from '@/components/modals/ChangePasswordModal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { appConfig } from '@/config/appConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/utils/error';
import { Calendar, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

export default function Login() {
  const navigate = useNavigate();
  const { login, user, isLoading: isAuthLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  // We can derive initial loading state from AuthContext
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Change Password Modal State
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [tempLoginData, setTempLoginData] = useState<{
    email: string;
    password?: string;
  }>({ email: '' });

  const redirectPath = searchParams.get('redirect') || '/dashboard';

  useEffect(() => {
    if (!isAuthLoading) {
      if (user) {
        navigate(redirectPath, { replace: true });
      } else {
        setIsCheckingAuth(false);
      }
    }
  }, [user, isAuthLoading, navigate, redirectPath]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const loginResponse: any = await authService.login(email, password);

      // Login returns tokens, so we need to fetch user details separately
      if (loginResponse && loginResponse.tokens) {
        const user = await authService.me();
        login(user); // Update context with full user object
      } else {
        throw new Error('Invalid login response');
      }

      toast({
        title: 'Success',
        description: 'You have been logged in successfully.',
      });

      navigate(redirectPath, { replace: true });
    } catch (error: unknown) {
      const apiError = error as ApiError;

      const isNewPasswordRequired =
        apiError.response?.status === 401 &&
        (apiError.response?.data?.error?.message?.includes('NEW_PASSWORD_REQUIRED') ||
          apiError.message?.includes('NEW_PASSWORD_REQUIRED'));

      if (isNewPasswordRequired) {
        setTempLoginData({ email, password });
        setShowChangePasswordModal(true);
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Login failed. Please check your credentials.';
        setError(errorMessage);
        toast({
          title: 'Login Failed',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setError('');

    const cognitoDomain = appConfig.cognito.domain;
    const clientId = appConfig.cognito.clientId;
    const redirectUri = appConfig.cognito.redirectUri;

    sessionStorage.setItem('auth_redirect', redirectPath);

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: redirectUri,
      prompt: 'select_account',
    });

    window.location.href = `${cognitoDomain}/oauth2/authorize?${params.toString()}`;
  };

  if (isCheckingAuth) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Checking your session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4'>
      <Card className='w-full max-w-md shadow-medium'>
        <CardHeader className='space-y-4 text-center'>
          <div className='mx-auto h-16 w-16 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center'>
            <Calendar className='h-9 w-9 text-white' />
          </div>
          <div>
            <CardTitle className='text-2xl'>{appConfig.orgName}</CardTitle>
            <CardDescription className='text-base'>Leave Management System</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailLogin} className='space-y-4'>
            {error && (
              <Alert variant='destructive'>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className='space-y-2'>
              <Label htmlFor='email'>Email</Label>
              <Input
                id='email'
                type='email'
                placeholder={`your@${appConfig.emailDomain}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label htmlFor='password'>Password</Label>
                <Link to='/forgot-password' className='text-xs text-primary hover:underline'>
                  Forgot password?
                </Link>
              </div>
              <Input
                id='password'
                type='password'
                placeholder='••••••••'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            <Button type='submit' className='w-full' disabled={isLoading}>
              {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Sign In
            </Button>
          </form>

          <div className='my-4 flex items-center gap-2'>
            <div className='h-px flex-1 bg-border' />
            <span className='text-xs text-muted-foreground'>OR</span>
            <div className='h-px flex-1 bg-border' />
          </div>

          <Button onClick={handleGoogleLogin} variant='outline' className='w-full' disabled={isLoading}>
            {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Continue with Google
          </Button>

          <p className='text-xs text-muted-foreground text-center mt-4'>
            By signing in, you agree to our terms of service and privacy policy.
          </p>
        </CardContent>
      </Card>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        email={tempLoginData.email}
        tempPassword={tempLoginData.password}
        onSuccess={async (newPassword) => {
          setError('');

          try {
            const loginResponse = await authService.login(tempLoginData.email, newPassword);

            if (loginResponse && loginResponse.tokens) {
              const loggedInUser = await authService.me();
              login(loggedInUser);
              navigate(redirectPath, { replace: true });
              return;
            }
          } catch {
            // Fall back to manual login with the new password prefilled.
          }

          setPassword(newPassword);
        }}
      />
    </div>
  );
}
